/** Display helpers. All formatting decisions live here so a status label
 *  reads the same on every page. */

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/** Renders an ISO date without the timezone shift a naive `new Date()` causes.
 *  A DATE column has no time; parsing it as UTC midnight and formatting it
 *  locally can move it back a day west of Greenwich. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const iso = value.slice(0, 10);
  const parts = iso.split('-');
  if (parts.length !== 3) return '—';
  const [y, m, d] = parts.map(Number) as [number, number, number];
  if (!y || !m || !d) return '—';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatNumber(value: number | null | undefined, unit = 'number'): string {
  if (value === null || value === undefined) return '—';
  switch (unit) {
    case 'currency':
      return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
    case 'percent':
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'hours':
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} hrs`;
    case 'days':
      return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} days`;
    case 'ratio':
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    default:
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
}

/**
 * Money, shortened, for places a full figure will not fit — dashboard tiles
 * and pipeline column headers.
 *
 * `$1.2M` beats `$1,240,000` on a 232px tile, and the exact figure is one
 * click away on the page the tile links to.
 */
export function formatCompactCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const sign = value < 0 ? '-' : '';
  const n = Math.abs(value);
  if (n >= 1_000_000) return `${sign}$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${sign}$${Math.round(n / 1000)}k`;
  if (n >= 1000) return `${sign}$${(n / 1000).toFixed(1)}k`;
  return `${sign}$${Math.round(n)}`;
}

/** Turns snake_case status values into readable labels. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/* Badge classes. The brand has no green or amber, so "good" is navy and
   everything worse climbs the crimson ramp. */

export function rockBadge(status: string): string {
  switch (status) {
    case 'complete':   return 'badge-good';
    case 'on_track':   return 'badge-good';
    case 'off_track':  return 'badge-critical';
    default:           return 'badge-neutral';
  }
}

export function issueBadge(priority: string): string {
  switch (priority) {
    case 'high':   return 'badge-critical';
    case 'medium': return 'badge-warning';
    default:       return 'badge-neutral';
  }
}

export function verdictBadge(verdict: string | null): string {
  switch (verdict) {
    case 'yes':    return 'badge-good';
    case 'unsure': return 'badge-warning';
    case 'no':     return 'badge-critical';
    default:       return 'badge-neutral';
  }
}

export function transitionBadge(status: string): string {
  switch (status) {
    case 'complete':
    case 'delegated':   return 'badge-good';
    case 'training':
    case 'documenting': return 'badge-warning';
    default:            return 'badge-neutral';
  }
}

/* --- Compliance -----------------------------------------------------------
   Crimson marks an open question, not an obligation. "Required" is a fact
   about the world and gets the navy treatment; "unreviewed" is work nobody
   has done and gets the accent. */

export function applicabilityBadge(value: string): string {
  switch (value) {
    case 'required':    return 'badge-good';
    case 'recommended': return 'badge-good';
    case 'unreviewed':  return 'badge-warning';
    default:            return 'badge-neutral';
  }
}

export function complianceStatusBadge(value: string): string {
  switch (value) {
    case 'maintained':  return 'badge-good';
    case 'remediating': return 'badge-serious';
    case 'assessing':   return 'badge-warning';
    default:            return 'badge-neutral';
  }
}

const ASSESSMENT_LABELS: Record<string, string> = {
  self: 'Self-assessed',
  third_party: 'Third-party assessed',
  self_or_third_party: 'Self or third-party',
};

export function assessmentLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return ASSESSMENT_LABELS[value] ?? humanise(value);
}

/* --- Sales ---------------------------------------------------------------
   Crimson on a deal means "this needs someone", never "this is a big deal" —
   otherwise the whole board lights up on a good quarter and the colour stops
   carrying information. */

export function dealBadge(deal: {
  IsWon?: boolean;
  IsClosed?: boolean;
  IsOverdue?: boolean;
  NextStepOverdue?: boolean;
}): string {
  if (deal.IsWon) return 'badge-good';
  if (deal.IsClosed) return 'badge-neutral';
  if (deal.IsOverdue) return 'badge-critical';
  if (deal.NextStepOverdue) return 'badge-warning';
  return 'badge-neutral';
}

/* `humanise` gets these nearly right and then produces "Co Managed" and
   "Qbr", so the ones that need a proper name get one. */

const SERVICE_LABELS: Record<string, string> = {
  managed_services: 'Managed services',
  co_managed: 'Co-managed IT',
  security: 'Security',
  cloud: 'Cloud',
  backup: 'Backup & DR',
  voice: 'Voice',
  project: 'Project',
  hardware: 'Hardware',
  other: 'Other',
};

export function serviceLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return SERVICE_LABELS[value] ?? humanise(value);
}

const ACTIVITY_LABELS: Record<string, string> = {
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  assessment: 'Assessment',
  proposal: 'Proposal',
  qbr: 'QBR',
  task: 'Task',
  note: 'Note',
};

export function activityLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return ACTIVITY_LABELS[value] ?? humanise(value);
}

/** Risk 1-25 mapped onto the five heat steps. */
export function heatClass(score: number): string {
  if (score >= 20) return 'heat-5';
  if (score >= 14) return 'heat-4';
  if (score >= 9)  return 'heat-3';
  if (score >= 5)  return 'heat-2';
  return 'heat-1';
}

/** Maturity 1-5 rendered as a label, matching the seeded level descriptors. */
/**
 * The maturity level a 1–5 score sits at, and what that level is called.
 *
 * ROUNDED, not floored: 1.7 is Level 2, because a department two-thirds of the
 * way through emerging is emerging, not reactive. Flooring would park almost
 * everything a rung lower than it reads and make the top level unreachable
 * without a perfect 5.
 */
export function maturityLevel(score: number | null): number | null {
  return score === null ? null : Math.min(5, Math.max(1, Math.round(score)));
}

const LEVEL_NAMES = ['Reactive', 'Emerging', 'Defined', 'Managed', 'Optimising'];

/** "Level 2 · Emerging" — the badge that appears beside every score. */
export function maturityLabel(score: number | null): string {
  const level = maturityLevel(score);
  if (level === null) return 'Not scored';
  return `Level ${level} · ${LEVEL_NAMES[level - 1]}`;
}

/**
 * Just the level's name — "Emerging" — with no "Level n" in front of it.
 *
 * For the places that already print the number themselves, which otherwise
 * read "L2 Level 2 · Emerging".
 */
export function maturityName(score: number | null): string {
  const level = maturityLevel(score);
  /* `maturityLevel` already clamps to 1-5, so the index is always in range;
     the fallback is what `noUncheckedIndexedAccess` wants to see. */
  return level === null ? 'Not rated' : (LEVEL_NAMES[level - 1] ?? 'Not rated');
}

/**
 * A score as a percentage of the scale, which is how the assessment is read
 * out loud — "40% mature" lands where "1.99 out of 5" does not.
 */
export function maturityPercent(score: number | null): number | null {
  return score === null ? null : Math.round((score / 5) * 100);
}

/**
 * The class a level wears: `lvl-1` .. `lvl-5`, or `lvl-none` when unscored.
 *
 * FIVE STEPS, NOT THREE. This used to fold the ladder onto the three status
 * tokens — good / warning / critical — and that failed in the one place it had
 * to work. `--status-warning-surface` and `--status-danger-surface` are both
 * crimson tints in this palette, because the brand has no amber, so levels 1
 * and 2 came out on the same pink background separated only by how dark the
 * text was. On a real assessment that is most of the page: ten departments at
 * Emerging and one at Reactive read as one undifferentiated red block, which
 * is the opposite of what a level badge is for.
 *
 * So the five levels climb the navy ramp instead, and crimson is kept for
 * level 1 alone — the one step that actually means "attend to this". That
 * keeps the Brand Direct rule (no green, no amber, crimson as accent) and
 * still gives every level its own mark. See the `.oml-badge` block in app.css
 * for the steps.
 *
 * Colour is never the only encoding: every place this is used prints the score
 * and the level name beside the mark.
 */
export function maturityToneClass(score: number | null): string {
  const level = maturityLevel(score);
  return level === null ? 'lvl-none' : `lvl-${level}`;
}

/**
 * Today as `YYYY-MM-DD` in the LOCAL timezone.
 *
 * `new Date().toISOString().slice(0,10)` is the obvious way to do this and is
 * wrong: it converts to UTC first, so anywhere west of Greenwich it returns
 * tomorrow's date all evening. These fields are business dates, so they must
 * follow the user's calendar, not UTC's.
 */
export function todayLocal(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Formats a Date as local `YYYY-MM-DD`, for the same reason as `todayLocal`. */
export function toLocalDateString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * A week of work, as the two dates a report or dial asks the API for.
 *
 * THE ONE DEFINITION OF A WEEK, and the reason it is here rather than in a
 * page: Reports › Billable Labor and the Service Delivery billable dial must
 * agree about which entries fall in a week, and two copies of this arithmetic
 * would eventually not.
 *
 * A week starts on MONDAY, and the boundary is drawn in THIS BROWSER's
 * timezone — the desk's own days, the same rule as the kill ratio's window.
 * The API turns the dates into instants (api/src/lib/billableLabor.ts).
 *
 * 'current' runs Monday to today, not Monday to Sunday: a week in progress
 * should read as what has been worked so far, not as a figure diluted by days
 * that have not happened. 'last' is the whole of the week before, Monday to
 * Sunday.
 */
export function weekRange(which: 'current' | 'last'): { from: string; to: string } {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  if (which === 'current') return { from: toLocalDateString(monday), to: todayLocal() };
  const start = new Date(monday);
  start.setDate(start.getDate() - 7);
  const end = new Date(monday);
  end.setDate(end.getDate() - 1);
  return { from: toLocalDateString(start), to: toLocalDateString(end) };
}

/** e.g. "2026-Q3" for the current quarter. */
export function currentQuarter(): string {
  const now = new Date();
  return `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`;
}

/* --- Projects ------------------------------------------------------------
   The brand has no green and no amber, so "on track" is navy and the two ways
   a project can be in trouble climb the crimson ramp. Health and status are
   separate badges because they answer different questions: whether the work is
   going well, and whether it is still open at all. */

const PROJECT_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  on_hold: 'On hold',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

export function projectStatusLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return PROJECT_STATUS_LABELS[value] ?? humanise(value);
}

/**
 * Closed work is deliberately colourless.
 *
 * A finished project is not an achievement the dashboard needs to celebrate in
 * navy, and a cancelled one is not a problem anybody can still act on — so both
 * read as history. Only 'on hold' gets a mark, because it is the status that
 * quietly stops work without anybody deciding to.
 */
export function projectStatusBadge(status: string): string {
  switch (status) {
    case 'active':  return 'badge-good';
    case 'on_hold': return 'badge-warning';
    default:        return 'badge-neutral';
  }
}

const PROJECT_HEALTH_LABELS: Record<string, string> = {
  on_track: 'On track',
  at_risk: 'At risk',
  needs_attention: 'Needs attention',
};

export function projectHealthLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return PROJECT_HEALTH_LABELS[value] ?? humanise(value);
}

export function projectHealthBadge(health: string): string {
  switch (health) {
    case 'on_track':        return 'badge-good';
    case 'at_risk':         return 'badge-warning';
    case 'needs_attention': return 'badge-critical';
    default:                return 'badge-neutral';
  }
}

/* =========================================================================
   Shared display helpers, promoted here when a second module needed them.

   `num` and `pct` were `pages/noc/nocFormat.ts`, which explained itself as
   separate from this file because it takes an ALREADY-COMPUTED percentage and a
   precision, which is not what `formatNumber` above does. That is still true and
   is why both exist — but the Admin module needs the same two, and one of them
   living in another module's folder was the wrong place for it.
   ========================================================================= */

/** A count or a measure, with a fixed number of decimals. An em dash for
 *  null, so "not recorded" never renders as 0. */
export function num(value: number | null | undefined, dp = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

/** An already-computed percentage. Does NOT multiply by 100 — callers pass 84.2
 *  for 84.2%, which is what every percentage column in the database holds. */
export function pct(value: number | null | undefined, dp = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${Number(value).toFixed(dp)}%`;
}

/**
 * The status bands, shared with the API.
 *
 * `neutral` is the shade for a chip with nothing to say. This brand has no green
 * and no amber, so the ladder runs good navy → warning → serious → critical
 * across two crimson steps — which is exactly why `StatusChip` also carries a
 * glyph and a label. See the note in styles/tokens.css.
 */
export type Band = 'good' | 'warning' | 'serious' | 'critical' | 'neutral';

/**
 * Seconds as a duration. A speed-to-answer of 41 reads as "41s"; a handle time
 * of 812 reads as "13m 32s" rather than as a number nobody converts in their
 * head.
 */
export function secs(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (value < 60) return `${Math.round(value)}s`;
  const m = Math.floor(value / 60);
  const s = Math.round(value % 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

/**
 * "in 12 days" / "3 mo overdue" — the phrasing every renewal, expiry and review
 * column in the Admin module uses.
 *
 * Switches to months past 60 days because "in 143 days" is a number nobody
 * converts either, and the precision is false comfort on a date that far out.
 */
export function relativeDays(days: number | null | undefined): string {
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 0) return days >= 60 ? `in ${Math.round(days / 30)} mo` : `in ${days} days`;
  const overdue = Math.abs(days);
  return overdue >= 60 ? `${Math.round(overdue / 30)} mo overdue` : `${overdue} days overdue`;
}

/**
 * The shared urgency scale for every date-driven register in the Admin module,
 * so a contract 20 days out and a licence 20 days out are the same colour.
 *
 * Past due is critical rather than merely serious: the deadline is gone, and
 * nothing about it improves by waiting.
 */
export function urgency(days: number | null | undefined): Band {
  if (days === null || days === undefined) return 'neutral';
  if (days < 0) return 'critical';
  if (days <= 30) return 'serious';
  if (days <= 90) return 'warning';
  return 'good';
}

/** "Mar 26" from a YYYY-MM period, for axis ticks and column headers. */
export function periodLabel(period: string): string {
  const parts = /^(\d{4})-(\d{2})$/.exec(period);
  if (!parts) return period;
  const month = Number(parts[2]);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[month - 1] ?? '?'} ${parts[1]?.slice(2) ?? ''}`;
}

/** "12 Mar" — for dense axis ticks, where the year is the same throughout and
 *  repeating it on every label spends the space for nothing. */
export function shortDate(iso: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!parts) return iso;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(parts[3])} ${MONTHS[Number(parts[2]) - 1] ?? '?'}`;
}

/** Day of week from the API's 0 = Sunday convention — see the weekday note in
 *  routes/adminAnalytics.ts. */
export function weekdayLabel(index: number): string {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index] ?? '?';
}

/**
 * A lower_snake enum value as a label: 'awaiting_approval' → 'Awaiting
 * approval'. Sentence case, not Title Case, because these appear in prose and
 * in table cells rather than as headings.
 *
 * The acronyms are spelled out explicitly — 'rma' would otherwise render as
 * 'Rma', 'sop' as 'Sop', and both look like typos.
 */
const ACRONYMS: Record<string, string> = {
  rma: 'RMA',
  sop: 'SOP',
  sops: 'SOPs',
  kpi: 'KPI',
  noc: 'NOC',
  hr: 'HR',
  oem: 'OEM',
  sow: 'SOW',
  csat: 'CSAT',
};

export function snakeLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const words = value.split('_');
  return words
    .map((word, i) => {
      const acronym = ACRONYMS[word];
      if (acronym) return acronym;
      /* Only the first word is capitalised — sentence case, see above. */
      return i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word;
    })
    .join(' ');
}

/**
 * Formats a value by the unit its KPI declares.
 *
 * Its own function rather than a call into `formatNumber` because the Admin
 * module's unit vocabulary is the database's (`percent`, `currency`, `seconds`,
 * `score`) and includes two that one does not handle.
 */
export function formatByUnit(value: number | null | undefined, unit: string): string {
  if (value === null || value === undefined) return '—';
  switch (unit) {
    case 'percent':
      return pct(value);
    case 'currency':
      return formatCompactCurrency(value);
    case 'seconds':
      return secs(value);
    case 'days':
      return `${num(value, 1)} d`;
    case 'hours':
      return `${num(value, 1)} h`;
    case 'count':
      return num(value);
    case 'score':
      return num(value, 2);
    default:
      return num(value, 1);
  }
}
